/**
 * @jsxRuntime classic
 * @jsx jsx
 */
/** @jsxFrag */
import React from 'react';

import { jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import AkFlag from '@atlaskit/flag/flag';
import type { FlagProps } from '@atlaskit/flag/types';
import SuccessIcon from '@atlaskit/icon/core/status-success';
import { Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { withLocalIntl } from '../../common/utils/i18n/intl-provider';

import { messages } from './messages';

export type Props = {
	/** Whether some teammates may require admin approval before they can access the product. */
	hasRequestAccess: boolean;
	/** Flag id passed through to AkFlag. */
	id: FlagProps['id'];
	/** Number of invites sent. */
	inviteCount: number;
	/** Called when the flag is dismissed. */
	onDismiss?: (id: FlagProps['id']) => void;
	/** Destination product the link points to. */
	productName: string;
};

/**
 * InviteOnThirdPartyLinkUsersAllHaveAccessFlag
 *
 * Shown when all linked users already have access to the resource.
 * This is an informational success flag; no CTA is rendered.
 *
 * In practice the consuming component may choose to suppress this flag entirely
 * rather than rendering it, depending on UX requirements.
 *
 * This is a placeholder implementation – messaging and behaviour will be refined
 * as part of GROOT-1060.
 */
export const InviteOnThirdPartyLinkInvitesSentFlagNoIntl = ({
	onDismiss,
	inviteCount,
	productName,
	hasRequestAccess,
	...props
}: Props): JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<AkFlag
			{...props}
			testId="invite-on-third-party-link-invites-sent-flag"
			icon={
				<SuccessIcon
					spacing="spacious"
					label={formatMessage(messages.titleInvitesSent, { inviteCount })}
					color={token('color.icon.success')}
				/>
			}
			onDismissed={onDismiss}
			title={formatMessage(messages.titleInvitesSent, { inviteCount })}
			description={
				<Stack space="space.050">
					<Text as="p">
						{formatMessage(messages.descriptionInvitesSent, { inviteCount, productName })}
					</Text>
					{hasRequestAccess && (
						<Text as="p">{formatMessage(messages.descriptionRequestAccess, { productName })}</Text>
					)}
				</Stack>
			}
		/>
	);
};

export const InviteOnThirdPartyLinkInvitesSentFlag: (props: Props) => React.JSX.Element =
	withLocalIntl(InviteOnThirdPartyLinkInvitesSentFlagNoIntl);
